import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";
import { mAuth } from "@/lib/api";
import { BlonixWordmark } from "@/components/BlonixLogo";
import { Input, PasswordRequirements } from "./MerchantLoginFields";
import { CountrySelect } from "@/components/CountrySelect";
import { MERCHANT, MERCHANT_LOGIN_ALIAS } from "@/lib/routes";
import {
  getEmailError,
  getPasswordError,
  getRequiredError,
  isValidPassword,
  normalizeEmail,
} from "@/lib/validation";

const emptyErrors = {
  company_name: "",
  contact_name: "",
  contact_email: "",
  password: "",
};

function validateSignupForm(form) {
  const errors = {
    company_name: getRequiredError(form.company_name, "Company name"),
    contact_name: getRequiredError(form.contact_name, "Your name"),
    contact_email: getEmailError(form.contact_email),
    password: getPasswordError(form.password),
  };
  return errors;
}

export default function MerchantSignup() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    company_name: "", contact_name: "", contact_email: "",
    password: "", country: "US", website: "",
  });
  const [errors, setErrors] = useState(emptyErrors);
  const [touched, setTouched] = useState({});
  const [loading, setLoading] = useState(false);

  const set = (k) => (v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((e) => ({ ...e, [k]: "" }));
  };

  const blur = (k) => () => {
    setTouched((t) => ({ ...t, [k]: true }));
    const fieldErrors = validateSignupForm(form);
    setErrors((e) => ({ ...e, [k]: fieldErrors[k] || "" }));
  };

  const showError = (k) => (touched[k] ? errors[k] : "");

  const onSubmit = async (e) => {
    e.preventDefault();
    const nextErrors = validateSignupForm(form);
    setErrors(nextErrors);
    setTouched({
      company_name: true,
      contact_name: true,
      contact_email: true,
      password: true,
    });

    if (Object.values(nextErrors).some(Boolean)) {
      toast.error("Please fix the highlighted fields");
      return;
    }

    if (!isValidPassword(form.password)) return;

    const payload = {
      ...form,
      contact_email: normalizeEmail(form.contact_email),
      company_name: form.company_name.trim(),
      contact_name: form.contact_name.trim(),
      website: form.website.trim() || undefined,
    };

    setLoading(true);
    try {
      await mAuth.signup(payload);
      toast.success("Account created. Signing you in…");
      await mAuth.login(payload.contact_email, form.password);
      navigate(MERCHANT.kyb);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Signup failed");
    } finally { setLoading(false); }
  };

  const formValid = Object.values(validateSignupForm(form)).every((msg) => !msg);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-zinc-100 p-6" data-testid="merchant-signup-page">
      <div className="w-full max-w-lg">
        <div className="flex flex-col items-center mb-8">
          <BlonixWordmark className="h-6 w-auto" />
          <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 mt-2">Merchant Portal</div>
        </div>

        <div className="bg-white border border-zinc-200 rounded-sm p-8">
          <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 mb-2">Get started</div>
          <h1 className="font-display text-3xl tracking-tight font-medium text-zinc-950">Create your merchant account</h1>
          <p className="mt-2 text-sm text-zinc-500">
            You'll start in <span className="font-mono">sandbox</span> mode. Production access activates after KYB approval.
          </p>

          <form onSubmit={onSubmit} className="mt-8 space-y-4" data-testid="m-signup-form" noValidate>
            <Input
              label="Company name"
              testId="signup-company"
              value={form.company_name}
              onChange={set("company_name")}
              onBlur={blur("company_name")}
              error={showError("company_name")}
            />
            <div className="grid grid-cols-2 gap-3">
              <Input
                label="Your name"
                testId="signup-contact"
                value={form.contact_name}
                onChange={set("contact_name")}
                onBlur={blur("contact_name")}
                error={showError("contact_name")}
              />
              <CountrySelect
                testId="signup-country"
                value={form.country}
                onChange={set("country")}
              />
            </div>
            <Input
              label="Work email"
              testId="signup-email"
              type="email"
              autoComplete="email"
              value={form.contact_email}
              onChange={set("contact_email")}
              onBlur={blur("contact_email")}
              error={showError("contact_email")}
            />
            <div>
              <Input
                label="Password"
                testId="signup-password"
                type="password"
                autoComplete="new-password"
                value={form.password}
                onChange={set("password")}
                onBlur={blur("password")}
                error={showError("password")}
              />
              <PasswordRequirements password={form.password} />
            </div>
            <Input label="Website (optional)" testId="signup-website" value={form.website} onChange={set("website")} />

            <button
              type="submit"
              disabled={loading || !formValid}
              data-testid="signup-submit-btn"
              className="w-full h-10 inline-flex items-center justify-center gap-2 bg-zinc-950 text-white text-sm font-medium rounded-sm hover:bg-zinc-800 transition-colors duration-150 disabled:opacity-50"
            >
              {loading ? "Creating…" : (<>Create account <ArrowRight size={14} /></>)}
            </button>

            <div className="text-xs text-zinc-500 text-center pt-2">
              Already a merchant?{" "}
              <Link to={MERCHANT_LOGIN_ALIAS} className="text-zinc-950 underline hover:no-underline">Sign in</Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
